import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  Avatar,
  CircularProgress,
  TextField,
  Chip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import SearchIcon from "@material-ui/icons/Search";
import utils from "../../universal/utils";

const styles = (theme) => ({
  root: {
    width: "100%",
  },
  productOption: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0),
  },
  productImage: {
    width: 60,
    height: 60,
    objectFit: "cover",
    borderRadius: 4,
    border: "1px solid #e0e0e0",
  },
  productDetails: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  productName: {
    fontWeight: 500,
    fontSize: "0.9rem",
    marginBottom: 2,
  },
  productMeta: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  productPrice: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: "0.9rem",
  },
  stockChip: {
    height: 20,
    fontSize: "0.7rem",
  },
});

const hebrewLabels = {
  searchLabel: "חפש מוצר לפי שם, מותג, גודל או EAN",
  placeholder: "הקלד לחיפוש...",
  loading: "טוען מוצרים...",
  noResults: "לא נמצאו מוצרים",
  stock: {
    inStock: "במלאי",
    lowStock: "מלאי נמוך",
    outOfStock: "אזל מהמלאי",
  },
};

const basePath = utils.getEdgeBasePath();

class ProductSelector extends React.Component {
  state = {
    products: [],
    loading: false,
  };

  componentDidMount() {
    this.loadProducts();
  }

  loadProducts = async () => {
    this.setState({ loading: true });

    try {
      const response = await fetch(`${basePath}/api/v1/admin/products/list`, {
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await response.json();

      if (data.ok) {
        console.log(`[ProductSelector] Loaded ${data.products.length} products`);
        // Log first product to debug field availability
        if (data.products.length > 0) {
          console.log("[ProductSelector] Sample product:", {
            displayName: data.products[0].displayName,
            brand: data.products[0].brand,
            name: data.products[0].name,
            displaySize: data.products[0].displaySize,
            ean: data.products[0].ean,
          });
        }
        this.setState({ products: data.products });
      }
    } catch (error) {
      console.error("Error loading products:", error);
    } finally {
      this.setState({ loading: false });
    }
  };

  getProductImageUrl = (product) => {
    if (!product || !product.cdn || !Array.isArray(product.cdn) || product.cdn.length === 0) {
      return null;
    }

    // Find primary image or use first image
    const primaryImage = product.cdn.find(
      (img) => img.primay === true || img.primary === true
    );
    const imageToUse = primaryImage || product.cdn[0];

    if (imageToUse && imageToUse.bucketPath) {
      return `https://s3.eu-west-2.amazonaws.com/sportyshop/${imageToUse.bucketPath}`;
    }

    return null;
  };

  formatProductLabel = (product) => {
    if (!product) return "";

    const parts = [];
    if (product.displayName) parts.push(product.displayName);
    else if (product.brand && product.name)
      parts.push(`${product.brand} ${product.name}`);

    if (product.displaySize) parts.push(product.displaySize);
    else if (product.size) parts.push(product.size);

    if (product.ean) parts.push(`EAN: ${product.ean}`);

    return parts.join(" - ");
  };

  getStockStatus = (stock) => {
    if (!stock || stock === 0) {
      return { label: hebrewLabels.stock.outOfStock, color: "secondary" };
    } else if (stock < 5) {
      return { label: hebrewLabels.stock.lowStock, color: "default" };
    }
    return { label: hebrewLabels.stock.inStock, color: "primary" };
  };

  handleProductChange = (event, product) => {
    if (this.props.onChange) {
      this.props.onChange(product);
    }
  };

  calculateRelevanceScore = (option, searchTerms) => {
    let score = 0;

    // Build searchable text from all relevant fields
    const searchableFields = {
      displayName: option.displayName || "",
      name: option.name || "",
      brand: option.brand || "",
      displaySize: option.displaySize || "",
      size: option.size || "",
      ean: option.ean || "",
      model: option.model || "",
    };

    // Calculate match quality (higher score for exact matches in important fields)
    searchTerms.forEach((term) => {
      // Exact match in displayName (highest priority)
      if (searchableFields.displayName.toLowerCase().includes(term)) {
        score += 100;
      }
      // Exact match in name
      if (searchableFields.name.toLowerCase().includes(term)) {
        score += 80;
      }
      // Match in brand
      if (searchableFields.brand.toLowerCase().includes(term)) {
        score += 60;
      }
      // Match in model
      if (searchableFields.model.toLowerCase().includes(term)) {
        score += 50;
      }
      // Match in size
      if (
        searchableFields.displaySize.toLowerCase().includes(term) ||
        searchableFields.size.toLowerCase().includes(term)
      ) {
        score += 40;
      }
      // Match in EAN (lower priority)
      if (searchableFields.ean.toLowerCase().includes(term)) {
        score += 20;
      }
    });

    // Boost score based on stock availability (products in stock are more relevant)
    const stock = option.stock || 0;
    if (stock > 0) {
      score += Math.min(stock * 2, 100); // Cap at +100 points
    }

    // Boost score if product has a score (indicates it has sales/metrics data)
    if (option.score && option.score > 0) {
      score += option.score; // Product score is 0-100
    }

    return score;
  };

  filterOptions = (options, { inputValue }) => {
    if (!inputValue || inputValue.trim() === "") {
      // No search term - return all products sorted by stock and score
      return options.slice().sort((a, b) => {
        const scoreA = (a.score || 0) + (a.stock || 0) * 2;
        const scoreB = (b.score || 0) + (b.stock || 0) * 2;
        return scoreB - scoreA;
      });
    }

    // Normalize and split search terms by space to allow multi-term search
    const searchTerms = inputValue
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter((term) => term.length > 0);

    if (searchTerms.length === 0) return options;

    console.log("[ProductSelector] Filtering with terms:", searchTerms);

    // Filter and score products
    const scoredProducts = options
      .map((option) => {
        // Build searchable text from all relevant fields
        const searchableFields = [
          option.displayName,
          option.name,
          option.brand,
          option.displaySize,
          option.size,
          option.ean,
          option.model,
        ];

        // Combine all fields into lowercase searchable text
        const searchableText = searchableFields
          .filter((field) => field != null && field !== "")
          .map((field) => String(field).toLowerCase())
          .join(" ");

        // Check if all search terms are present
        const matches = searchTerms.every((term) => searchableText.includes(term));

        if (!matches) return null;

        // Calculate relevance score
        const relevanceScore = this.calculateRelevanceScore(option, searchTerms);

        return {
          product: option,
          score: relevanceScore,
        };
      })
      .filter((item) => item !== null);

    // Sort by relevance score (highest first)
    scoredProducts.sort((a, b) => b.score - a.score);

    // Extract just the products
    const filtered = scoredProducts.map((item) => item.product);

    console.log(
      `[ProductSelector] Filtered ${filtered.length} products from ${options.length} total`
    );
    if (filtered.length > 0 && scoredProducts.length > 0) {
      console.log(
        `[ProductSelector] Top result: ${filtered[0].displayName || filtered[0].name} (score: ${scoredProducts[0].score})`
      );
    }

    return filtered;
  };

  render() {
    const { classes, value } = this.props;
    const { products, loading } = this.state;

    return (
      <div className={classes.root}>
        <Autocomplete
          options={products}
          getOptionLabel={this.formatProductLabel}
          getOptionSelected={(option, value) => option.ean === value.ean}
          value={value || null}
          onChange={this.handleProductChange}
          loading={loading}
          filterOptions={this.filterOptions}
          disableListWrap
          ListboxProps={{
            style: { maxHeight: 400 },
          }}
          renderOption={(option) => {
            const imageUrl = this.getProductImageUrl(option);
            const stockStatus = this.getStockStatus(option.stock);

            return (
              <Box className={classes.productOption}>
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={option.displayName || option.name || "Product"}
                    className={classes.productImage}
                  />
                ) : (
                  <Avatar className={classes.productImage}>
                    {(
                      option.brand ||
                      option.displayName ||
                      option.name ||
                      "P"
                    )[0].toUpperCase()}
                  </Avatar>
                )}
                <Box className={classes.productDetails}>
                  <Typography className={classes.productName}>
                    {(
                      option.displayName ||
                      option.name ||
                      option.brand ||
                      "מוצר"
                    ).trim()}
                    {(option.displaySize || option.size) &&
                      ` ${(option.displaySize || option.size).trim()}`}
                  </Typography>
                  <Box className={classes.productMeta}>
                    <Typography variant="caption">
                      EAN: {option.ean}
                    </Typography>
                    {option.price && (
                      <Typography variant="caption" className={classes.productPrice}>
                        ₪{option.price}
                      </Typography>
                    )}
                    <Chip
                      label={stockStatus.label}
                      size="small"
                      color={stockStatus.color}
                      className={classes.stockChip}
                    />
                  </Box>
                </Box>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={hebrewLabels.searchLabel}
              variant="outlined"
              placeholder={hebrewLabels.placeholder}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <React.Fragment>
                    <SearchIcon style={{ marginRight: 8, marginLeft: 8, color: "rgba(0, 0, 0, 0.54)" }} />
                    {params.InputProps.startAdornment}
                  </React.Fragment>
                ),
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          noOptionsText={hebrewLabels.noResults}
          loadingText={hebrewLabels.loading}
        />
      </div>
    );
  }
}

ProductSelector.propTypes = {
  classes: PropTypes.object.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func,
};

export default withStyles(styles)(ProductSelector);
